/**
 * A paleta de blocos do construtor de fichas.
 *
 * Dois grupos, porque são duas decisões diferentes. Os **genéricos** montam
 * qualquer pergunta: texto, número, escolha, tabela, anexo. Os do
 * **profissional** trazem vocabulário clínico publicado — Fitzpatrick, Glogau,
 * Baumann, a classificação da OMS para o IMC — e por isso não são configuráveis
 * como um campo livre: mexer nas faixas seria mexer em conduta clínica.
 *
 * O que a paleta oferece, o preenchimento sabe renderizar e o prontuário sabe
 * ler. Um bloco que grava e ninguém consegue responder é pior do que um bloco
 * que não existe.
 */

import type { TipoDeCampoDaFicha } from '@/data/tipos';

export interface BlocoDaPaleta {
    tipo: TipoDeCampoDaFicha;
    nome: string;
    icone: string;
    ajuda: string;
    /** Tipo que só faz sentido com opções: sem elas não há o que responder. */
    temOpcoes?: boolean;
    /** Bloco do pacote clínico — escolhe-se qual escala, não os rótulos. */
    doProfissional?: boolean;
}

export const PALETA: BlocoDaPaleta[] = [
    { tipo: 'texto', nome: 'Texto', icone: 'type', ajuda: 'Uma linha — nome, alergia, medicamento.' },
    { tipo: 'texto-longo', nome: 'Texto longo', icone: 'align-left', ajuda: 'Relato, conduta, evolução.' },
    { tipo: 'texto-rico', nome: 'Editor de texto', icone: 'bold', ajuda: 'Negrito, itálico e lista — para conduta que precisa de destaque.' },
    { tipo: 'numero', nome: 'Número', icone: 'hash', ajuda: 'Peso, medida, dosagem.' },
    { tipo: 'data', nome: 'Data', icone: 'calendar', ajuda: 'Última aplicação, início do quadro.' },
    { tipo: 'selecao-unica', nome: 'Seleção única', icone: 'circle-dot', ajuda: 'Uma resposta entre as opções.', temOpcoes: true },
    { tipo: 'selecao-multipla', nome: 'Seleção múltipla', icone: 'list-checks', ajuda: 'Quantas se aplicarem.', temOpcoes: true },
    { tipo: 'tabela', nome: 'Tabela', icone: 'table', ajuda: 'Linhas e colunas que você nomeia — evolução por sessão, por região.' },
    { tipo: 'arquivo', nome: 'Envio de arquivos', icone: 'paperclip', ajuda: 'Foto, exame, termo assinado. Fica no cofre da clínica.' },

    {
        tipo: 'escala', nome: 'Escala ilustrada', icone: 'images', doProfissional: true,
        ajuda: 'Fitzpatrick, celulite, cicatrizes de acne — 24 escalas com a arte do acervo.',
    },
    {
        tipo: 'tabela-selecionavel', nome: 'Tabela selecionável', icone: 'rows-3', doProfissional: true,
        ajuda: 'Glogau e grau da acne: a linha inteira é o alvo, porque é ela que se lê para decidir.',
    },
    {
        tipo: 'escala-encadeada', nome: 'Escala encadeada', icone: 'git-branch', doProfissional: true,
        ajuda: 'Tipo de pele de Baumann: quatro dimensões que montam a sigla.',
    },
    {
        tipo: 'calculo-imc', nome: 'Cálculo de IMC', icone: 'calculator', doProfissional: true,
        ajuda: 'Peso e altura, com a classificação da OMS calculada na hora.',
    },
    {
        tipo: 'perimetria', nome: 'Perimetria', icone: 'ruler', doProfissional: true,
        ajuda: 'Circunferências e a relação cintura/quadril.',
    },
    {
        tipo: 'adipometria', nome: 'Adipometria', icone: 'git-commit-horizontal', doProfissional: true,
        ajuda: 'Dobras cutâneas: somatório, ou percentual de gordura por Jackson & Pollock.',
    },
];

/**
 * O que este editor ainda não monta.
 *
 * A ausência dita em voz alta, dentro da tela, para não parecer esquecimento.
 * Está vazia desde que a adipometria entrou — o que ficou de fora dela não é um
 * bloco, é a FAIXA de classificação do percentual de gordura, e essa ausência
 * está dita no próprio bloco, onde quem lê o número a procuraria.
 */
export const PENDENTES: string[] = [];

export const NOME_DO_TIPO: Record<TipoDeCampoDaFicha, string> = {
    'texto': 'Texto',
    'texto-longo': 'Texto longo',
    'texto-rico': 'Editor de texto',
    'numero': 'Número',
    'data': 'Data',
    'selecao-unica': 'Seleção única',
    'selecao-multipla': 'Seleção múltipla',
    'tabela': 'Tabela',
    'arquivo': 'Envio de arquivos',
    'escala': 'Escala ilustrada',
    'tabela-selecionavel': 'Tabela selecionável',
    'escala-encadeada': 'Escala encadeada',
    'calculo-imc': 'Cálculo de IMC',
    'perimetria': 'Perimetria',
    'adipometria': 'Adipometria',
};

export const TIPO_TEM_OPCOES: TipoDeCampoDaFicha[] = ['selecao-unica', 'selecao-multipla'];

/** Blocos cuja configuração é escolher qual escala do catálogo. */
export const TIPO_TEM_ESCALA: TipoDeCampoDaFicha[] = ['escala', 'escala-encadeada', 'tabela-selecionavel'];

/** Blocos cujo resultado muda com o sexo — e que dizem isso quando ele falta. */
export const TIPO_TEM_SEXO: TipoDeCampoDaFicha[] = ['calculo-imc', 'perimetria', 'adipometria'];

/** Bloco cuja configuração é escolher qual protocolo publicado ele aplica. */
export const TIPO_TEM_PROTOCOLO: TipoDeCampoDaFicha[] = ['adipometria'];

let sequencia = 0;

/** Identificador do campo. É a chave da resposta — nunca reaproveitar. */
export function novoUid(): string {
    sequencia += 1;
    return `c${Date.now().toString(36)}${sequencia.toString(36)}`;
}
